import { Check, ChevronDown } from 'lucide-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import {
  ChainlitContext,
  useAuth,
  useChatInteract,
  useChatMessages,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card';

import { IAttachment, attachmentsState } from '@/state/chat';

import { NewChatDialog } from './NewChat';

interface Props {
  navigate?: (to: string) => void;
}

export default function ChatProfiles({ navigate }: Props) {
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const { user } = useAuth();
  const { chatProfile, setChatProfile, hotSwapChatProfile } = useChatSession();
  const { firstInteraction } = useChatMessages();
  const { clear } = useChatInteract();
  const setAttachments = useSetRecoilState<IAttachment[]>(attachmentsState);
  const [newChatProfile, setNewChatProfile] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const hotSwapEnabled = !!config?.features?.hot_swap_chat_profile;

  const filteredProfiles = useMemo(() => {
    return (
      config?.chatProfiles.filter((p) => {
        if (user) return true;
        return p.allow_anonymous !== false;
      }) || []
    );
  }, [config?.chatProfiles, user]);

  // Handle case when no profile is selected
  useEffect(() => {
    if (!chatProfile && filteredProfiles.length > 0) {
      setChatProfile(filteredProfiles[0].name);
    }
  }, [chatProfile, filteredProfiles, setChatProfile]);

  // Handle case when selected profile becomes invalid
  useEffect(() => {
    if (chatProfile) {
      const profileExists = filteredProfiles.some(
        (profile) => profile.name === chatProfile
      );
      if (!profileExists && filteredProfiles.length > 0) {
        setChatProfile(filteredProfiles[0].name);
      }
    }
  }, [chatProfile, filteredProfiles, setChatProfile]);

  const handleClose = () => {
    setOpenDialog(false);
    setNewChatProfile(null);
    navigate?.('/');
  };

  const handleConfirm = (profile: string) => {
    if (hotSwapEnabled) {
      if (!hotSwapChatProfile(profile)) {
        setChatProfile(profile);
        setAttachments([]);
        clear();
        handleClose();
      }
      setNewChatProfile(null);
      setOpenDialog(false);
      return;
    }

    setChatProfile(profile);
    setNewChatProfile(null);
    setAttachments([]);
    clear();
    handleClose();
  };

  const allowHtml = config?.features?.unsafe_allow_html;
  const latex = config?.features?.latex;

  // Early return check to prevent unnecessary renders and resource waste
  if (filteredProfiles.length <= 1) {
    return null;
  }

  const selectedProfile = useMemo(() => {
    return filteredProfiles.find((p) => p.name === chatProfile);
  }, [filteredProfiles, chatProfile]);

  const selectedProfileIcon = useMemo(() => {
    if (!selectedProfile?.icon) return null;
    return selectedProfile.icon.includes('/public')
      ? apiClient.buildEndpoint(selectedProfile.icon)
      : selectedProfile.icon;
  }, [selectedProfile, apiClient]);

  return (
    <div className="relative">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            id="chat-profiles"
            variant="ghost"
            className="flex items-center gap-1 px-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            {selectedProfileIcon ? (
              <img
                src={selectedProfileIcon}
                alt={
                  selectedProfile?.display_name ||
                  selectedProfile?.name ||
                  'Profile'
                }
                className="w-6 h-6 rounded-md object-cover shrink-0"
              />
            ) : (
              <span className="font-semibold text-lg flex items-center justify-center w-6 h-6 shrink-0">
                {chatProfile?.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="hidden sm:inline-block font-medium text-sm truncate max-w-[150px] ml-1 text-foreground">
              {selectedProfile?.display_name ||
                selectedProfile?.name ||
                chatProfile}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {filteredProfiles.map((profile) => {
            const icon = profile.icon?.includes('/public')
              ? apiClient.buildEndpoint(profile.icon)
              : profile.icon;

            return (
              <HoverCard openDelay={0} closeDelay={0} key={profile.name}>
                <HoverCardTrigger asChild>
                  <DropdownMenuItem
                    data-test={`select-item:${profile.name}`}
                    onClick={() => {
                      setNewChatProfile(profile.name);
                      if (firstInteraction && !hotSwapEnabled) {
                        setOpenDialog(true);
                      } else {
                        handleConfirm(profile.name);
                      }
                    }}
                    className="cursor-pointer flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      {icon && (
                        <img
                          src={icon}
                          alt={profile.display_name || profile.name}
                          className="w-6 h-6 rounded-md object-cover"
                        />
                      )}
                      <span>{profile.display_name || profile.name}</span>
                    </div>
                    {chatProfile === profile.name && (
                      <Check className="h-4 w-4 ml-2" />
                    )}
                  </DropdownMenuItem>
                </HoverCardTrigger>
                <HoverCardContent
                  side="right"
                  id="chat-profile-description"
                  align="start"
                  className="w-80 overflow-visible"
                  sideOffset={10}
                >
                  <Markdown
                    allowHtml={allowHtml}
                    latex={latex}
                    renderMarkdown={true}
                  >
                    {profile.markdown_description}
                  </Markdown>
                </HoverCardContent>
              </HoverCard>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <NewChatDialog
        open={openDialog}
        handleClose={handleClose}
        handleConfirm={() => newChatProfile && handleConfirm(newChatProfile)}
      />
    </div>
  );
}
